import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SessionStorageService } from '../services/session-storage.service';
import { RightBridgeApiService } from '../services/right-bridge-api.service';
import { EnvironmentService } from '../services/environment.service';
import { ErrorReportingService } from '../services/error-reporting.service';
import {
  Carriers,
  ProcessItem,
  ProfileCaseStats,
  ValidateProducts,
} from '../models/li-models';

@Injectable({
  providedIn: 'root',
})
export class LifeWizardService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private router = inject(Router);
  private rbs = inject(RightBridgeApiService);
  private envSvc = inject(EnvironmentService);
  private errSvc = inject(ErrorReportingService);

  private environment;
  private baseUrl: string;
  private org: string;

  constructor() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.org = this.environment.org;
  }

  getLifeNeeds(profile): Observable<ProcessItem> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'li')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');

    const url = `${this.baseUrl}/Process/LifeNeeds/${profile}`;

    return this.http.get<ProcessItem>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getLifeScenarios(profile): Observable<ProcessItem> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'li')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');

    const url = `${this.baseUrl}/Process/LifeScenarios/${profile}?includeAll=true`;

    return this.http.get<ProcessItem>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getLifeProducts(profile, includeAll = false): Observable<ProcessItem> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'li')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const url = `${this.baseUrl}/Process/LifeProducts/${profile}?includeAll=${includeAll}`;

    return this.http.get<ProcessItem>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getValidateCarriers(): Observable<Carriers> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'li')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const url = `${this.baseUrl}/LifeProducts/UnitCarriers`;

    return this.http.get<Carriers>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getValidateProducts(group, state): Observable<ValidateProducts> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'li')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const url = `${this.baseUrl}/LifeProducts/UnitProductsByCarrier?carrierGroupID=${group}&stateOfIssue=${state}`;

    return this.http.get<ValidateProducts>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getReport(profile, prods, silentPrint?): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'li')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    let url = `${this.baseUrl}/reports/li/${profile}`;

    if (silentPrint) {
      url += '&returnReport=false';
    }

    return this.http
      .post(url, prods, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getLifeStats(): Observable<ProfileCaseStats> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'li')
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');

    const url = `${this.baseUrl}/widget/ProfileCaseStats`;

    return this.http.get<ProfileCaseStats>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }
}
